import { formatCurrency } from '@angular/common';
import { AsYouType, CountryCode } from 'libphonenumber-js';
import { ToString } from 'src/lib/fliko/helpers/toString.helper';
import { RentalUnit, RentalUnitType } from 'src/lib/fliko/models/units/unit.data';
import { DateFormat, LocalizationHelper, NumberFormat } from 'src/app/shared/helpers/localization.helper';
import { MatIconHlp } from 'src/app/shared/helpers/matIcon.helper';
import { LocLabel, LocLabelModifier } from './data-table-config.models';

export interface ValueTable<T> {
  value: T;
  displayValue: string;
  className?: string;
}

export interface RentalUnitTableVM {
  unit: RentalUnit;
  type: RentalUnitType;
}

export class ValueCustomTable<T> implements ValueTable<T> {
  constructor(
    public value: T,
    public displayValue: string,
    public classname?: string
  ) {}
}

export class ValueNumberTable implements ValueTable<number> {
  constructor(
    public value: number,
    public locHelper: LocalizationHelper,
    public format: NumberFormat = 'twoDec',
    public className?: string,
    public unit?: string
  ) {}

  public get displayValue(): string {
    const formatted = this.locHelper.formatNumber(this.value, this.format);
    return this.unit ? formatted + ' ' + this.unit : formatted;
  }
}

// FIXME: change to handle currency codes
export class ValueCurrencyTable implements ValueTable<number> {
  constructor(
    public value: number,
    public currency: string,
    public className?: string,
    public locale: string = 'pl-PL'
  ) {}

  public get displayValue(): string {
    return formatCurrency(this.value, this.locale, this.currency);
  }
}

export class ValueDateTable implements ValueTable<number> {
  constructor(
    public value: number,
    public format: DateFormat = 'dShort',
    public className?: string
  ) {}

  public get displayValue(): string {
    throw new Error("Don't use displayValue for ValueDateTable!");
  }
}

export class ValueStringTable implements ValueTable<string> {
  constructor(
    public value: string,
    public className?: string
  ) {}
  public get displayValue() {
    return this.value;
  }
}

// TODO: replace all translated cells
export class ValueStringLocalizedTable implements ValueTable<string> {
  constructor(
    public value: string,
    public replaceObj?: any,
    public modifiers?: LocLabelModifier,
    public className?: string
  ) {}
  public get displayValue() {
    return this.value;
  }
}

export class ValueButtonTable implements ValueTable<LocLabel | string> {
  constructor(
    public value: LocLabel | string,
    public handle: (event: PointerEvent) => void,
    public color: string = 'primary',
    public buttonClass: string = 'mat-raised-button'
  ) {}
  public get displayValue() {
    return typeof this.value === 'string' ? this.value : this.value.key;
  }
}

export class ValueIconTable implements ValueTable<string> {
  constructor(
    public value: string,
    public tooltipKey?: string,
    public color: string = 'primary',
    public className?: string
  ) {}
  public get displayValue() {
    return this.value;
  }
}

export class ValueIconButtonTable implements ValueTable<string> {
  constructor(
    public value: string,
    public handle: (event: PointerEvent) => void,
    public color: string = 'primary',
    public className?: string
  ) {}

  public get displayValue() {
    return this.value;
  }
}

export class ValueNumberTooltipTable implements ValueTable<number> {
  constructor(
    public value: number,
    public tooltip: string
  ) {}

  public get displayValue() {
    return this.value.toString();
  }
}

export class ValueEnumTable<T> implements ValueTable<T> {
  constructor(
    public value: T,
    public displayParser: (x: T) => string
  ) {}
  public get displayValue() {
    return this.displayParser(this.value);
  }
}

export class ValueUnitTable implements ValueTable<number> {
  constructor(
    public value: number,
    public unit: string,
    public locHelper: LocalizationHelper
  ) {}
  public get displayValue() {
    return `${this.locHelper.formatNumber(this.value, 'twoDec')} ${this.unit}`;
  }
}

export class ValuePhoneNumberTable implements ValueTable<string | null | undefined> {
  public className = '';
  public isValid = true;
  constructor(
    public value: string | null | undefined,
    public placeholder: string = '',
    public locale: CountryCode = 'PL'
  ) {
    const formatter = new AsYouType(locale);
    const formatted = formatter.input(value ?? '');
    this.className = formatted ? '' : 'mat-warn';
    this.isValid = !!formatted;
    if (value) value = value.replace(/\s/g, '');
  }

  public get displayValue() {
    return this.value ?? '';
  }
}

export interface TableUnitVM {
  number: string;
  icon: string;
}

export class ValueRentalUnitAdrTable implements ValueTable<string> {
  private _displayArray: TableUnitVM[] = [];
  private _value = '';

  constructor(
    public data: RentalUnitTableVM[],
    longNumber: boolean = false
  ) {
    const tempArr: TableUnitVM[] = [];
    this._value = '';
    data.forEach((val) => {
      const number = longNumber ? ToString.fullNumber(val.unit, '-', false) : ToString.shortNumber(val.unit);
      tempArr.push({ number, icon: MatIconHlp.get(val.type) });
      this._value += number.toLowerCase() + '|';
    });
    this._displayArray = tempArr.sort((a, b) => MatIconHlp.sort(a.icon, b.icon));
  }

  public get value(): string {
    return this._value;
  }

  public get displayArray(): TableUnitVM[] {
    return this._displayArray;
  }

  public get displayValue(): string {
    return '';
  }
}

/**
 * Checkbox in clickable row table; value is rowId!!
 */
export class ValueCheckboxTable implements ValueTable<string> {
  public checked = false;

  /** Value = rowId! */
  constructor(
    public value: string,
    public onClick: (id: string, checked: boolean) => void,
    public defaultValue: boolean = false,
    public disabled: boolean = false
  ) {
    this.checked = this.defaultValue;
  }

  public get displayValue() {
    return '';
  }

  public click(value: boolean) {
    if (this.disabled) {
      return;
    }
    this.checked = value;
    this.onClick(this.value, this.checked);
  }
}

export class ValueResidentUserTable implements ValueTable<string> {
  constructor(
    public value: string,
    public userName: string | null
  ) {}
  public get displayValue() {
    return `${this.value} ${this.userName ? `(${this.userName})` : ''}`;
  }
}
